<template>
  <div :class="[show ? 'open' : '']" class="accordion">
    <div class="course-detail">
      <div class="top-row">
        <div class="intro">
          <p class="title">{{ course.title }}</p>
          <p class="dates">{{ formattedDates }}</p>
        </div>
        <button type="button" class="faculty-button" @click="showBio = true">
          <div class="faculty-image">
            <img class="photo" :src="course.bio_image || $assets.image.default['bio_default_rd.svg']" alt="" />
          </div>
          <p class="faculty-name">Faculty: {{ course.faculty_name }}</p>
        </button>
      </div>
      <p class="description">{{ course.info_description }}</p>

      <div class="info-boxes">
        <div class="info-box">
          <p class="topics-title">Course Topics:</p>
          <ul class="info-list">
            <li v-for="(topic, index) in course.info_topics" :key="`${topic}-${index}`">{{ topic }}</li>
          </ul>
        </div>
        <div class="info-box">
          <p class="skills-title">Skills Used:</p>
          <ul class="info-list">
            <li v-for="(skill, index) in course.info_skills" :key="`${skill}-${index}`">{{ skill }}</li>
          </ul>
        </div>
      </div>

      <div class="buttons">
        <RegisterOrVideoButton :evEventOrCourse="course" />
        <MaterialsButton @click="showCourseMaterials = true" />
      </div>
    </div>

    <div class="header">
      <span class="course-info">
        Course {{ numbers[number] }} | {{ course.title }} | {{ formattedDate(course.start_date) }}
      </span>
      <button type="button" class="view-detail" @click="toggleItem">
        <span class="button-text">View Details</span>
        <inline-svg :src="$assets.svg['detail_triangle']" class="button-icon" :width="'17px'" :height="'17px'" />
      </button>
    </div>

    <CourseMaterialsPopup
      v-if="showCourseMaterials"
      :pdfLink="course.pdf_link"
      :docLink="course.doc_link"
      :pptLink="course.ppt_link"
      :videoLink="course.video_link"
      @close="showCourseMaterials = false"
    />
    <BioPopup v-if="showBio" :bio="bio" @close="showBio = false" />
  </div>
</template>

<script>
import BioPopup from '@/components/events/BioPopup.vue';
import CourseMaterialsPopup from '@/components/events/CourseMaterialsPopup.vue';
import MaterialsButton from '@/components/events/MaterialsButton.vue';
import RegisterOrVideoButton from '@/components/events/RegisterOrVideoButton.vue';

export default {
  data() {
    return {
      showCourseMaterials: false,
      showBio: false,
      numbers: ['One', 'Two', 'Three', 'Four'],
      show: false,
    };
  },

  props: {
    course: {
      type: Object,
      default: () => {
        return {};
      },
    },
    number: {
      type: Number,
      default: null,
    },
  },

  computed: {
    formattedDates() {
      if (!this.course.start_date || !this.course.end_date) return 'Coming Soon';

      const dateOptions = { dateStyle: 'long' };
      const date = this.course.start_date.toLocaleString('en-CA', dateOptions);

      const timeOptions = { hour: 'numeric', minute: 'numeric' };
      const startTime = this.course.start_date.toLocaleString('en-CA', timeOptions);
      const endTime = this.course.end_date.toLocaleString('en-CA', timeOptions);

      return `${date} | ${startTime} - ${endTime}`;
    },
    bio() {
      // eslint-disable-next-line camelcase
      const { bio_name, bio_information, bio_image, bio_linkedin } = this.course;

      return {
        bio_name,
        bio_information,
        bio_image,
        bio_linkedin,
      };
    },
  },

  components: {
    CourseMaterialsPopup,
    BioPopup,
    MaterialsButton,
    RegisterOrVideoButton,
  },

  methods: {
    toggleItem() {
      this.show = !this.show;
    },
    formattedDate(date) {
      if (!date) return 'Coming Soon';

      const options = { dateStyle: 'long' };
      return date.toLocaleString('en-CA', options);
    },
  },
};
</script>

<style scoped>
.accordion {
  --header-height: 4.75em;
}

/** Larger devices */
@media only screen and (min-width: 601px) {
  .accordion {
    --header-height: 3.75em;
  }
}

.accordion {
  position: relative;
  max-height: var(--header-height);
  margin-top: 20px;
  background: #ffffff;

  transition: 0.5s ease all;
  overflow: hidden;

  border-radius: 5px;
  box-shadow: 0 2px 16px 0 rgba(0, 0, 0, 0.5);
}

.accordion.open {
  max-height: 1575px;
}

.header {
  position: absolute;
  top: 0%;
  height: var(--header-height);
  width: 100%;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  font-family: Helvetica;
  color: #201c1c;

  background: #defaff;
  border-radius: 5px;
}

.course-info {
  font-size: 1.15em;
  margin: 0 10px;
}

.view-detail {
  height: 3.5em;
  width: 9em;

  font-family: Helvetica;
  font-size: 1.15em;
  color: #0091ff;
  background: transparent;
  cursor: pointer;

  border: none;
  border-left: 1px solid #d8d8d8;

  display: flex;
  align-items: center;
  justify-content: space-around;
}

.view-detail .button-text {
  display: none;
}

.course-detail {
  position: relative;
  height: calc(100% - 3.75em);
  width: calc(100% - 2em);
  padding: 0 1em;
  padding-top: var(--header-height);
  padding-bottom: 4em;

  box-shadow: 0 2px 16px 0 rgba(0, 0, 0, 0.5);
  border-radius: 5px;

  font-family: Helvetica;
  font-size: 1em;
  color: #392929;

  transition: 0.5s ease all;
}

.top-row {
  margin-top: 1.25em;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.intro > p {
  margin: 0.2em 0;
}

.faculty-button {
  background: transparent;
  border: none;
  cursor: pointer;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.faculty-image {
  height: 3em;
  width: 3em;
  border-radius: 50%;
  background: linear-gradient(#28cede, #56b4ff);

  overflow: hidden;

  flex-shrink: 0;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.faculty-image > .photo {
  height: 90%;
  width: 90%;
  border-radius: 50%;

  object-fit: cover;
}

.faculty-name {
  margin-left: 0.5em;
  color: #392929;
}

.course-description {
  font-family: Helvetica;
  font-size: 1em;
  color: #333333;
}

.info-boxes {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
}

.info-box {
  width: 100%;
  flex-grow: 1;

  margin-top: 2em;
  padding: 0 2em 3em 2em;

  background: #f3eef3;
  border-radius: 0.7em;
}

.info-list {
  padding-left: 1em;
}

.info-list > li {
  margin: 0.5em 0;

  list-style-type: disc;
  color: #333333;
}

.detail-enter-active,
.detail-leave-active {
  transition: 0.5s all ease;
  overflow: hidden;
}
.detail-enter,
.detail-leave-to {
  height: var(--header-height);
}

.buttons {
  width: 22em;
  margin-top: 1.5em;

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
}

/** Larger devices */
@media only screen and (min-width: 601px) {
  .course-info {
    margin: 0 25px;
  }

  .view-detail .button-text {
    display: block;
  }

  .course-detail {
    width: calc(100% - 4em);
    padding: 0 2em;
    padding-top: var(--header-height);
    padding-bottom: 4em;
  }

  .info-boxes {
    gap: 3em;
  }

  .info-box {
    width: calc(45% - 4em);
    min-width: 350px;
  }
}
</style>
